import FilterPopoverTabs from '@/Enums/Attribution/FilterPopoverTabs.enum';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import { PixelsTabButton, PlacementsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/advertisers';
import { FILTERS_BASE_TAB_ID } from '@/constants/attribution';
import { PRIMARY } from '@/constants/color';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectWebVisitsFilterSource, selectWebVisitsFilters } from '@/store/selectors/attribution/webVisits.selectors';
import { setFilters } from '@/store/slices/attribution/webVisits.slice';
import { a11yProps } from '@/utils/tabProps';
import { Add, ArrowDropDown } from '@mui/icons-material';
import { Button, Tab, Tabs, popoverClasses, tabClasses } from '@mui/material';
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

const FilterPopover = () => {
  const dispatch = useAppDispatch();

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const filtersOpen = useMemo<boolean>(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const [currentTab, setCurrentTab] = useState<FilterPopoverTabs>(FilterPopoverTabs.Pixels);

  const { placementOptions, eventOptions, filterSourceLoading } = useAppSelector(selectWebVisitsFilterSource);
  const {
    advertisers: filterAdvertisers,
    campaigns: filterCampaigns,
    placement: filterPlacement,
    events: filterEvents,
  } = useAppSelector(selectWebVisitsFilters);

  const [pendingPlacements, setPendingPlacements] = useState<IControlOption[]>([]);
  const [pendingEvents, setPendingEvents] = useState<IControlOption[]>([]);

  const filtersId = useMemo(() => (filtersOpen ? 'additional-filters-popover' : undefined), [filtersOpen]);

  useEffect(() => {
    if (filterCampaigns.length > 0) {
      setCurrentTab(FilterPopoverTabs.Placements);
    } else {
      setCurrentTab(FilterPopoverTabs.Pixels);
    }
  }, [filterCampaigns, setCurrentTab]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const addFiltersCount = useMemo(
    () => (filterPlacement?.length ? 1 : 0) + (filterEvents?.length ? 1 : 0),
    [filterPlacement.length, filterEvents.length],
  );

  const resetPendingValues = useCallback(() => {
    setPendingPlacements(filterPlacement);
    setPendingEvents(filterEvents);
  }, [filterPlacement, setPendingEvents, filterEvents, filterPlacement]);

  const handleOpen = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues, setFilterAnchorEl],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FilterPopoverTabs.Pixels);
  }, [setFilterAnchorEl, resetPendingValues, setCurrentTab]);

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: FilterPopoverTabs) => {
      setCurrentTab(newValue);
    },
    [setCurrentTab],
  );

  const updatePlacementlValue = useCallback(
    (updatedPlacements: IControlOption[] = []) => {
      setPendingPlacements(updatedPlacements);
    },
    [setPendingPlacements],
  );

  const updateEventsValue = useCallback(
    (updatedEvents: IControlOption[] = []) => {
      setPendingEvents(updatedEvents);
    },
    [setPendingEvents],
  );

  const handleSubmit = useCallback(() => {
    if (
      areArraysOfObjectsEqual(filterPlacement, pendingPlacements) &&
      areArraysOfObjectsEqual(filterEvents, pendingEvents)
    ) {
      handleClose();
      return;
    }

    dispatch(
      setFilters({
        placement: pendingPlacements,
        events: pendingEvents,
      }),
    );
    handleClose();
  }, [dispatch, filterPlacement, pendingPlacements, filterEvents, pendingEvents]);

  return (
    <>
      <Button
        variant='contained'
        onClick={handleOpen}
        aria-describedby={filtersId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: '37px',
          marginTop: '15px',
        }}
        disabled={filterSourceLoading || !filterAdvertisers.length}
      >
        <Add sx={{ width: 16, height: 16 }} />
        <span>Add Filter</span>
        {!!addFiltersCount && (
          <div className={`bg-white rounded-full w-5 text-[${PRIMARY}] text-sm`}>{addFiltersCount}</div>
        )}
        <ArrowDropDown sx={{ width: 16, height: 16 }} />
      </Button>
      <TabFilterPopover
        id={filtersId}
        anchorEl={filterAnchorEl}
        open={filtersOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          [`& .${popoverClasses.paper}`]: {
            width: 1000,
            maxWidth: 1000,
          },
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          <Tab
            value={FilterPopoverTabs.Placements}
            component={PlacementsTabButton}
            disabled={!filterCampaigns.length}
            {...a11yProps(FilterPopoverTabs.Placements, FILTERS_BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Pixels}
            component={PixelsTabButton}
            disabled={!!filterCampaigns.length}
            {...a11yProps(FilterPopoverTabs.Pixels, FILTERS_BASE_TAB_ID)}
          />
        </Tabs>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Placements}>
          <TabSelect
            label='Placement'
            options={placementOptions}
            value={pendingPlacements}
            onChange={updatePlacementlValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Pixels}>
          <TabSelect
            label='Pixel'
            options={eventOptions}
            value={pendingEvents}
            onChange={updateEventsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
